// src/pages/ForgotPasswordPage.jsx
import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import "./Auth.css";

function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);

  const readHashParams = () => {
    const hash = window.location.hash?.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    return new URLSearchParams(hash);
  };

  // URL에서 토큰 확인 (비밀번호 재설정 링크 클릭 시)
  useEffect(() => {
    let mounted = true;

    const bootstrapRecoverySession = async () => {
      const hashParams = readHashParams();
      const recoveryFlag =
        window.sessionStorage.getItem("password_recovery_mode") === "1";
      const queryType = searchParams.get("type");
      const hashType = hashParams.get("type");
      const queryAccessToken = searchParams.get("access_token");
      const hashAccessToken = hashParams.get("access_token");
      const code = searchParams.get("code");
      const isRecoveryType =
        queryType === "recovery" || hashType === "recovery" || recoveryFlag;

      // PKCE(code)와 hash(access_token) 링크 모두 대응
      if (isRecoveryType && code && supabase) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError && mounted) {
          setError(exchangeError.message || "재설정 링크 인증에 실패했습니다.");
        }
      }

      if (
        mounted &&
        isRecoveryType &&
        (queryAccessToken || hashAccessToken || code)
      ) {
        setIsResetMode(true);
      }
    };

    bootstrapRecoverySession();

    return () => {
      mounted = false;
    };
  }, [searchParams]);

  // 비밀번호 재설정 요청 (이메일 전송)
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);

    if (!supabase) {
      setError("Supabase가 설정되지 않았습니다. .env 파일을 확인하세요.");
      setLoading(false);
      return;
    }

    try {
      const redirectTo = `${window.location.origin}/forgot-password?type=recovery`;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email,
        { redirectTo }
      );

      if (resetError) {
        const redirectErrorMessage = String(resetError.message || "").toLowerCase();
        const isRedirectConfigError =
          redirectErrorMessage.includes("redirect") ||
          redirectErrorMessage.includes("not allowed") ||
          redirectErrorMessage.includes("invalid");

        if (!isRedirectConfigError) {
          throw resetError;
        }

        // Redirect URL 허용 목록 불일치 시 기본 Site URL 기반 링크로 한 번 더 시도
        const { error: fallbackError } = await supabase.auth.resetPasswordForEmail(
          email
        );
        if (fallbackError) {
          throw fallbackError;
        }
      }

      setMessage("비밀번호 재설정 링크가 이메일로 전송되었습니다. 이메일을 확인해주세요.");
    } catch (err) {
      console.error("Password reset error:", {
        message: err?.message,
        status: err?.status,
        code: err?.code,
        name: err?.name,
        details: err,
      });
      const rawMessage = err?.message || "";
      const lowerMessage = String(rawMessage).toLowerCase();
      if (lowerMessage.includes("redirect") || lowerMessage.includes("not allowed")) {
        setError(
          "재설정 링크 발송에 실패했습니다. Supabase 대시보드의 Redirect URL 허용 목록에 현재 주소를 추가해주세요."
        );
      } else if (lowerMessage.includes("sending recovery email")) {
        setError(
          "재설정 메일 발송 단계에서 실패했습니다. Supabase Auth의 이메일(Reset Password) 설정 또는 SMTP 설정을 확인해주세요."
        );
      } else {
        setError(rawMessage || "비밀번호 재설정 요청 중 오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  // 비밀번호 재설정 (토큰을 사용하여 새 비밀번호 설정)
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("새 비밀번호를 입력해주세요.");
      return;
    }

    if (newPassword.length < 6) {
      setError("비밀번호는 최소 6자 이상이어야 합니다.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("새 비밀번호가 일치하지 않습니다.");
      return;
    }

    if (!supabase) {
      setError("Supabase가 설정되지 않았습니다. .env 파일을 확인하세요.");
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
        data: {
          password_changed: true,
        },
      });

      if (updateError) {
        throw updateError;
      }

      window.sessionStorage.removeItem("password_recovery_mode");
      setMessage("비밀번호가 성공적으로 변경되었습니다. 로그인 페이지로 이동합니다.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      console.error("Password update error:", err);
      setError(err.message || "비밀번호 변경 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* 왼쪽 설명 영역 - 로그인/회원가입이랑 맞춰서 */}
        <div className="auth-left">
          <h1 className="portal-title">비밀번호 재설정</h1>
          <p className="portal-desc">
            계정에 다시 접근할 수 있도록 비밀번호 재설정 링크를 보내드립니다.
          </p>
          <ul className="portal-benefits">
            <li>회사를 떠났거나 이메일이 변경된 경우 HR 담당자에게 문의하세요.</li>
            <li>보안 강화를 위해 주기적인 비밀번호 변경을 권장합니다.</li>
            <li>비밀번호는 다른 서비스와 겹치지 않도록 설정해 주세요.</li>
          </ul>
        </div>

        {/* 오른쪽 폼 */}
        <div className="auth-right">
          <h2 className="auth-section-title">비밀번호 찾기</h2>
          <p className="auth-section-desc">
            가입하신 회사 이메일 주소를 입력하시면,
            비밀번호 재설정 안내 메일을 보내드립니다.
          </p>

          {!isResetMode ? (
            // 비밀번호 재설정 요청 폼
            <form className="auth-form" onSubmit={handleRequestReset}>
              <label className="auth-label">
                이메일
                <input
                  type="email"
                  className="auth-input"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>

              {error && <div className="auth-error">{error}</div>}
              {message && <div className="auth-success">{message}</div>}

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? "요청 중..." : "재설정 링크 보내기"}
              </button>
            </form>
          ) : (
            // 새 비밀번호 설정 폼
            <form className="auth-form" onSubmit={handleResetPassword}>
              <label className="auth-label">
                새 비밀번호
                <input
                  type="password"
                  className="auth-input"
                  placeholder="새 비밀번호를 입력하세요 (최소 6자)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </label>

              <label className="auth-label">
                새 비밀번호 확인
                <input
                  type="password"
                  className="auth-input"
                  placeholder="새 비밀번호를 다시 입력하세요"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </label>

              {error && <div className="auth-error">{error}</div>}
              {message && <div className="auth-success">{message}</div>}

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? "변경 중..." : "비밀번호 변경"}
              </button>
            </form>
          )}

          <div className="auth-footer">
            로그인 페이지로 돌아가기{" "}
            <Link to="/login" className="auth-link">
              로그인 화면으로 이동
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;